import { useState, useRef, useCallback } from 'react';
import { useMutationCallbacks } from '@/hooks/useMutationCallbacks';
import { SubscribeToPlanMutation } from '@/features/subscription/graphql/SubscribeToPlanMutation';
import type { SubscribeToPlanMutation as SubscribeToPlanMutationType } from '@/__generated__/SubscribeToPlanMutation.graphql';
import { emptyCard, emptyHolderInfo } from '@/features/subscription/components/CardPaymentForm';
import type { DiscountInfo } from '@/features/subscription/components/PromoCodeInput';
import type { Step, PaymentResult } from '@/features/subscription/types/subscriptionStep';
import type { SavedCard } from '@/features/subscription/utils/cardUi';

interface SubscribeFlowInputs {
  isAsaas: boolean;
  savedCards: SavedCard[];
  plansForLookup: ReadonlyArray<{ slug: string; name: string; valueCents?: number; comingSoon?: boolean | null }>;
  /** When set, the flow opens pre-selected on this plan (jumps straight to its payment step). */
  initialPlan?: string | null;
}

interface PlanClickArgs {
  planSlug: string;
  planName: string;
  valueCents: number;
  isFree: boolean;
}

type PlanLookup = ReadonlyArray<{ slug: string; name: string; valueCents?: number; comingSoon?: boolean | null }>;

/**
 * Computes the starting step from an optional pre-selected plan. A paid plan
 * with Asaas opens straight at the payment step; anything else (no intent,
 * free, coming-soon, unknown slug, non-Asaas) starts at the plan list.
 * Used as a lazy `useState` initializer — runs once during render, no mount effect.
 */
const computeInitialStep = (initialPlan: string | null | undefined, plans: PlanLookup, isAsaas: boolean): Step => {
  if (!initialPlan) return { type: 'plans' };
  const plan = plans.find((p) => p.slug === initialPlan);
  if (!plan || plan.valueCents === undefined || plan.comingSoon) return { type: 'plans' };
  if (plan.valueCents > 0 && isAsaas) {
    return { type: 'method', planSlug: plan.slug, planName: plan.name, valueCents: plan.valueCents };
  }
  return { type: 'plans' };
};

interface SubmitArgs {
  planSlug: string;
  creditCard?: typeof emptyCard;
  creditCardHolderInfo?: typeof emptyHolderInfo;
  savedCardId?: string;
  withAutoCharge?: boolean;
}

/**
 * Encapsulates the subscription flow's state machine plus mutation handling.
 *
 * Bug fixes applied here (vs the previous inline logic in SubscriptionModal):
 *   - Fix 1: card and holderInfo are reset on EVERY submit completion (success
 *     OR error), not only on success. Avoids leaking sensitive card data in
 *     React state after a failed payment.
 *   - Fix 2: handlePlanClick disables further clicks immediately by setting
 *     `isHandlingClick`. The UI gates the button with `isHandlingClick ||
 *     isPending` so rapid clicks dispatch a single mutation.
 *   - Fix 3: an AbortController is created lazily; `reset()` (called when the
 *     modal closes) signals abort. afterCompleted/afterError short-circuit if
 *     the request was aborted, so a closed modal doesn't corrupt UI state on
 *     reopen.
 */
export const useSubscribeFlow = ({ isAsaas, savedCards, plansForLookup, initialPlan }: SubscribeFlowInputs) => {
  const [step, setStep] = useState<Step>(() => computeInitialStep(initialPlan, plansForLookup, isAsaas));
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState(emptyCard);
  const [holderInfo, setHolderInfo] = useState(emptyHolderInfo);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [autoChargeExcess, setAutoChargeExcess] = useState(false);
  const [isHandlingClick, setIsHandlingClick] = useState(false);
  const [promotionalCode, setPromotionalCode] = useState<string | null>(null);
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const clearCardData = useCallback(() => {
    setCard(emptyCard);
    setHolderInfo(emptyHolderInfo);
  }, []);

  const [commitSubscribe, isPending] = useMutationCallbacks<SubscribeToPlanMutationType>({
    mutation: SubscribeToPlanMutation,
    name: 'SubscribeToPlan',
    shouldShowSnackbar: false,
    afterCompleted: (response) => {
      // Fix 3: ignore the resolved value if the modal was closed mid-submit.
      if (abortRef.current?.signal.aborted) {
        clearCardData();
        setIsHandlingClick(false);
        return;
      }

      const result = response.SubscribeToPlan;
      // Fix 1: clear sensitive card data on every completion, success or not.
      clearCardData();

      if (result?.brCode || result?.paymentLinkUrl) {
        const plan = plansForLookup.find((p) => p.slug === result.subscription?.plan);
        const paymentResult: PaymentResult = {
          brCode: result.brCode ?? null,
          paymentLinkUrl: result.paymentLinkUrl ?? null,
          planName: plan?.name ?? '',
        };
        setStep({ type: 'result', result: paymentResult });
      } else {
        const plan = plansForLookup.find((p) => p.slug === result?.subscription?.plan);
        const paymentResult: PaymentResult = { planName: plan?.name ?? '', isAutoCharged: true };
        setStep({ type: 'result', result: paymentResult });
      }

      setIsHandlingClick(false);
    },
    afterError: (errorMessage) => {
      // Fix 3: ignore errors from aborted requests.
      if (abortRef.current?.signal.aborted) {
        clearCardData();
        setIsHandlingClick(false);
        return;
      }
      // Fix 1: clear card on error too.
      clearCardData();
      setError(typeof errorMessage === 'string' ? errorMessage : 'Erro ao assinar plano. Tente novamente.');
      setIsHandlingClick(false);
    },
  });

  const submitSubscribe = useCallback(({ planSlug, creditCard, creditCardHolderInfo, savedCardId, withAutoCharge }: SubmitArgs) => {
    setError(null);
    if (!abortRef.current) abortRef.current = new AbortController();
    const includeAutoCharge = withAutoCharge !== undefined ? withAutoCharge : autoChargeExcess;
    commitSubscribe({
      variables: {
        input: {
          plan: planSlug as any,
          autoChargeExcess: includeAutoCharge,
          ...(promotionalCode ? { promotionalCode } : {}),
          ...(creditCard ? { creditCard } : {}),
          ...(creditCardHolderInfo ? { creditCardHolderInfo } : {}),
          ...(savedCardId ? { savedCardId } : {}),
        },
      },
    });
  }, [commitSubscribe, autoChargeExcess, promotionalCode]);

  const handlePlanClick = useCallback(({ planSlug, planName, valueCents, isFree }: PlanClickArgs) => {
    if (isHandlingClick || isPending) return; // Fix 2: ignore rapid double-clicks
    setIsHandlingClick(true);
    setError(null);

    if (isFree) {
      submitSubscribe({ planSlug });
      return;
    }

    if (isAsaas) {
      setStep({ type: 'method', planSlug, planName, valueCents });
      setIsHandlingClick(false);
      return;
    }

    // Woovi (PIX-only): submit directly
    submitSubscribe({ planSlug });
  }, [isHandlingClick, isPending, isAsaas, submitSubscribe]);

  const goToCardStep = useCallback(({ planSlug, planName, valueCents }: { planSlug: string; planName: string; valueCents: number }) => {
    if (savedCards.length > 0) {
      setStep({ type: 'card_selector', planSlug, planName, valueCents, savedCards });
    } else {
      setStep({ type: 'card', planSlug, planName, valueCents });
    }
  }, [savedCards]);

  const handleBack = useCallback(() => {
    setError(null);
    setStep((current) => {
      switch (current.type) {
        case 'method':
          return { type: 'plans' };
        case 'card_selector':
        case 'card':
          return { type: 'method', planSlug: current.planSlug, planName: current.planName, valueCents: current.valueCents };
        default:
          return current;
      }
    });
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStep({ type: 'plans' });
    setError(null);
    clearCardData();
    setSelectedCardId('');
    setAutoChargeExcess(false);
    setIsHandlingClick(false);
    setPromotionalCode(null);
    setDiscountInfo(null);
  }, [clearCardData]);

  return {
    // state
    step,
    error,
    card,
    holderInfo,
    selectedCardId,
    autoChargeExcess,
    isPending: isPending || isHandlingClick,
    promotionalCode,
    discountInfo,
    // setters
    setCard,
    setHolderInfo,
    setSelectedCardId,
    setAutoChargeExcess,
    setPromotionalCode,
    setDiscountInfo,
    setStep,
    setError,
    // actions
    handlePlanClick,
    submitSubscribe,
    goToCardStep,
    handleBack,
    reset,
  };
};
